import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';
import { addActivity } from '../services/activityService';
import apiService from '../services/apiService';

const MatchForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    type: 'upcoming',
    homeTeam: {
      name: 'FC ივერია',
      logo: 'https://via.placeholder.com/64x64/3B82F6/FFFFFF?text=FC'
    },
    awayTeam: {
      name: '',
      logo: 'https://via.placeholder.com/64x64/EF4444/FFFFFF?text=AW'
    },
    date: new Date().toISOString().split('T')[0],
    time: '18:00',
    venue: 'ივერია არენა',
    tournament: 'ეროვნული ლიგა',
    score: ''
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const loadMatch = async () => {
        try {
          const match = await apiService.getMatchById(id);
          if (match) {
            setFormData({
              type: match.type,
              homeTeam: match.homeTeam,
              awayTeam: match.awayTeam,
              date: match.date,
              time: match.time,
              venue: match.venue,
              tournament: match.tournament,
              score: match.score || ''
            });
          }
        } catch (error) {
          console.error('Error loading match:', error);
        }
      };
      loadMatch();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTeamChange = (team, field, value) => {
    setFormData(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Check authentication before making the request
      if (!apiService.isAuthenticated()) {
        alert('თქვენ არ ხართ ავტორიზებული. გთხოვთ, შეხვიდეთ სისტემაში.');
        navigate('/admin/login');
        return;
      }

      if (isEdit) {
        await apiService.updateMatch(id, formData);
        addActivity('match', 'updated', {
          title: `${formData.homeTeam.name} vs ${formData.awayTeam.name}`,
          description: `მატჩი "${formData.homeTeam.name} vs ${formData.awayTeam.name}" განახლდა`
        });
      } else {
        await apiService.createMatch(formData);
        addActivity('match', 'created', {
          title: `${formData.homeTeam.name} vs ${formData.awayTeam.name}`,
          description: `ახალი მატჩი "${formData.homeTeam.name} vs ${formData.awayTeam.name}" დაემატა`
        });
      }
      navigate('/_panel-7c39f8/matches');
    } catch (error) {
      console.error('Error saving match:', error);
      
      // Handle authentication errors specifically
      if (error.message.includes('Authentication failed')) {
        alert('თქვენი სესია ამოიწურა. გთხოვთ, შეხვიდეთ თავიდან სისტემაში.');
        navigate('/admin/login');
      } else {
        alert('მატჩის შენახვა ვერ მოხერხდა. გთხოვთ, სცადოთ თავიდან.');
      }
    } finally {
      setSaving(false);
    }
  };

  const matchTypes = [
    { value: 'upcoming', label: 'შემდეგი მატჩი' },
    { value: 'previous', label: 'წინა მატჩი' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title={isEdit ? 'მატჩის რედაქტირება' : 'ახალი მატჩი'}
        subtitle={isEdit ? 'შეცვალეთ მატჩის ინფორმაცია' : 'დაამატეთ ახალი მატჩი'}
      />

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Match Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                მატჩის ტიპი
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              >
                {matchTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Teams */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Home Team */}
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  სახლის გუნდი
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formData.homeTeam.name}
                    onChange={(e) => handleTeamChange('homeTeam', 'name', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="გუნდის სახელი"
                    required
                  />
                  <ImageUpload
                    value={formData.homeTeam.logo}
                    onChange={(url) => handleTeamChange('homeTeam', 'logo', url)}
                    category="logos"
                    label="საშინაო გუნდის ლოგო"
                    required={false}
                  />
                </div>
              </div>

              {/* Away Team */}
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  სტუმარი გუნდი
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formData.awayTeam.name}
                    onChange={(e) => handleTeamChange('awayTeam', 'name', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="გუნდის სახელი"
                    required
                  />
                  <ImageUpload
                    value={formData.awayTeam.logo}
                    onChange={(url) => handleTeamChange('awayTeam', 'logo', url)}
                    category="logos"
                    label="სტუმარი გუნდის ლოგო"
                    required={false}
                  />
                </div>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  თარიღი
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  დრო
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Venue and Tournament */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  <MapPinIcon className="h-4 w-4 inline mr-1" />
                  სტადიონი
                </label>
                <input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="სტადიონის სახელი"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ტურნირი
                </label>
                <input
                  type="text"
                  name="tournament"
                  value={formData.tournament}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="ტურნირის სახელი"
                />
              </div>
            </div>

            {/* Score (for previous matches) */}
            {formData.type === 'previous' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ქულა
                </label>
                <input
                  type="text"
                  name="score"
                  value={formData.score}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="მაგ: 2:1"
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/matches')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-georgian"
              >
                {saving ? 'შენახვა...' : (isEdit ? 'განახლება' : 'შექმნა')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MatchForm;
